import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  Maximize2,
  Minimize2,
  Minus,
  Plus,
  RotateCcw,
  X,
} from "lucide-react";

import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/utils";
import { fileDownloadUrl } from "@/shared/lib/authenticated-file";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  FileViewerContext,
  type FileViewerContextValue,
  type ViewableFile,
} from "@/shared/components/file-viewer-context";

export type { ViewableFile };

type Kind =
  | "image"
  | "pdf"
  | "video"
  | "audio"
  | "office"
  | "text"
  | "unknown";

function extensionOf(name: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(name || "");
  return m ? m[1].toLowerCase() : "";
}

function classify(file: ViewableFile): Kind {
  const mime = (file.mime_type || "").toLowerCase();
  const ext = extensionOf(file.file_name);
  if (mime.startsWith("image/") || ["jpg", "jpeg", "png", "webp", "gif", "svg"].includes(ext)) {
    return "image";
  }
  if (mime === "application/pdf" || ext === "pdf") return "pdf";
  if (mime.startsWith("video/") || ["mp4", "mov", "webm", "ogg"].includes(ext)) {
    return "video";
  }
  if (mime.startsWith("audio/") || ["mp3", "wav", "m4a"].includes(ext)) {
    return "audio";
  }
  if (
    ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext) ||
    mime === "application/msword" ||
    mime === "application/vnd.ms-excel" ||
    mime === "application/vnd.ms-powerpoint" ||
    mime ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    mime ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    mime ===
      "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  ) {
    return "office";
  }
  if (mime.startsWith("text/") || ["txt", "csv", "log", "json"].includes(ext)) {
    return "text";
  }
  return "unknown";
}

function isPublicAbsoluteUrl(url: string): boolean {
  try {
    const u = new URL(url, window.location.origin);
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    const host = u.hostname;
    if (
      host === "localhost" ||
      host === "127.0.0.1" ||
      host.endsWith(".local") ||
      /^192\.168\./.test(host) ||
      /^10\./.test(host) ||
      /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(host)
    ) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 5;
const ZOOM_STEP = 0.25;
// A click on the image jumps by a larger stride than the +/- buttons so a
// single click gives a satisfying "zoom into this spot" feel.
const CLICK_ZOOM_STEP = 0.5;
const DRAG_THRESHOLD = 3;
const clampZoom = (z: number) =>
  Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100));

type ImageViewState = { scale: number; x: number; y: number };
const RESET_VIEW: ImageViewState = { scale: 1, x: 0, y: 0 };

function ImageView({ file }: { file: ViewableFile }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<ImageViewState>(RESET_VIEW);
  const [dragging, setDragging] = useState(false);
  // ``file_url`` is the stable, never-expiring file-view proxy URL (or a public
  // media URL). It carries its own capability token, so a bare <img src> loads
  // it directly - no blob fetch, no session header, and never a stale signed
  // URL (TA-23).
  const imgSrc = file.file_url ?? undefined;
  // Pointer-gesture bookkeeping: where the drag began, the view offset at
  // that moment, and whether the pointer has moved enough to count as a pan
  // (vs. a click-to-zoom).
  const gesture = useRef<{
    x: number;
    y: number;
    ox: number;
    oy: number;
    moved: boolean;
  } | null>(null);

  // Reset zoom + pan when the viewed file changes.
  useEffect(() => {
    setView(RESET_VIEW);
  }, [file.file_uuid, file.file_url]);

  // Zoom toward a screen point (clientX/clientY), keeping whatever is under
  // the cursor pinned in place. Omit the point to zoom about the centre
  // (used by the +/- buttons). `computeNext` receives the current scale so
  // rapid wheel events accumulate correctly.
  const zoomAtPoint = useCallback(
    (
      computeNext: (prevScale: number) => number,
      clientX?: number,
      clientY?: number,
    ) => {
      const el = containerRef.current;
      setView((prev) => {
        const next = clampZoom(computeNext(prev.scale));
        if (next === prev.scale) return prev;
        if (next === 1 || !el) {
          return next === 1 ? RESET_VIEW : { ...prev, scale: next };
        }
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = (clientX ?? cx) - cx;
        const dy = (clientY ?? cy) - cy;
        const ratio = next / prev.scale;
        // Derived so the image point under the cursor stays under the cursor:
        // offset' = d*(1 - ratio) + ratio*offset, d = cursor - centre.
        return {
          scale: next,
          x: dx * (1 - ratio) + ratio * prev.x,
          y: dy * (1 - ratio) + ratio * prev.y,
        };
      });
    },
    [],
  );

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    gesture.current = {
      x: e.clientX,
      y: e.clientY,
      ox: view.x,
      oy: view.y,
      moved: false,
    };
    if (view.scale > 1) setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (!g.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) g.moved = true;
    if (view.scale > 1 && g.moved) {
      setView((prev) => ({ ...prev, x: g.ox + dx, y: g.oy + dy }));
    }
  };

  const endGesture = (e: React.PointerEvent) => {
    const g = gesture.current;
    gesture.current = null;
    setDragging(false);
    // A press that didn't pan is a click: zoom in toward the cursor, wrapping
    // back to fit once we pass the max.
    if (g && !g.moved && e.button === 0) {
      zoomAtPoint(
        (s) => (s >= ZOOM_MAX ? 1 : s + CLICK_ZOOM_STEP),
        e.clientX,
        e.clientY,
      );
    }
  };

  const cursorClass =
    view.scale <= 1
      ? "cursor-zoom-in"
      : dragging
        ? "cursor-grabbing"
        : "cursor-grab";

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative flex min-h-0 flex-1 select-none items-center justify-center overflow-hidden bg-[var(--bg-light)] touch-none",
        cursorClass,
      )}
      onWheel={(e) => {
        // Wheel zooms toward the cursor; deltaY<0 (scroll up) zooms in.
        e.preventDefault();
        zoomAtPoint((s) => s - e.deltaY * 0.0015, e.clientX, e.clientY);
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endGesture}
      onPointerCancel={endGesture}
    >
      <img
        src={imgSrc ?? ""}
        alt={file.file_name}
        draggable={false}
        className={cn(
          "max-h-full max-w-full object-contain",
          !dragging && "transition-transform duration-100",
        )}
        // Scale + pan are runtime-computed values, so they stay inline per the
        // dynamic-style exception.
        style={{
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
        }}
      />
      <div
        className="absolute bottom-3 right-3 flex items-center gap-1 rounded-lg border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-1 py-1 shadow-[var(--shadow-float)]"
        // Keep clicks on the controls from starting an image drag/zoom.
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => zoomAtPoint((s) => s - ZOOM_STEP)}
          disabled={view.scale <= ZOOM_MIN}
          aria-label="Zoom out"
        >
          <Minus className="h-4 w-4" />
        </Button>
        <button
          type="button"
          onClick={() => setView(RESET_VIEW)}
          className="flex min-w-[44px] items-center justify-center gap-1 px-1 text-[11px] tabular-nums text-[var(--ink-2)] hover:text-[var(--ink)]"
          aria-label="Reset zoom"
        >
          {view.scale !== 1 ? <RotateCcw className="h-3 w-3" /> : null}
          {Math.round(view.scale * 100)}%
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => zoomAtPoint((s) => s + ZOOM_STEP)}
          disabled={view.scale >= ZOOM_MAX}
          aria-label="Zoom in"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function PdfView({ file }: { file: ViewableFile }) {
  // ``file_url`` is same-origin in production (Firebase rewrites /api/** to
  // Cloud Run) and carries its own token, so it embeds directly - no blob
  // shuttle needed. A missing url surfaces the fallback.
  if (!file.file_url) {
    return <UnsupportedView file={file} reason="error" />;
  }
  return (
    <iframe
      src={file.file_url}
      title={file.file_name}
      className="min-h-0 flex-1 w-full border-0 bg-[var(--bg-light)]"
    />
  );
}

/** Does this file have any resolvable source at all? */
function hasSource(file: ViewableFile): boolean {
  return !!file.file_url;
}

function VideoView({ file }: { file: ViewableFile }) {
  if (!file.file_url) {
    return <UnsupportedView file={file} reason="error" />;
  }
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center bg-black">
      <video src={file.file_url} controls className="max-h-full max-w-full" />
    </div>
  );
}

function AudioView({ file }: { file: ViewableFile }) {
  if (!file.file_url) {
    return <UnsupportedView file={file} reason="error" />;
  }
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center bg-[var(--bg-light)] p-6">
      <audio src={file.file_url} controls className="w-full max-w-xl" />
    </div>
  );
}

function OfficeView({ file }: { file: ViewableFile }) {
  const url = file.file_url ?? "";
  // Office Online viewer can only fetch URLs reachable from the public
  // internet. Localhost / private-network media URLs (typical in dev)
  // would render an error inside the iframe, so we surface a fallback
  // instead.
  if (!isPublicAbsoluteUrl(url)) {
    return <UnsupportedView file={file} reason="dev" />;
  }
  const src = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
  return (
    <iframe
      src={src}
      title={file.file_name}
      className="min-h-0 flex-1 w-full border-0 bg-[var(--bg-light)]"
    />
  );
}

function TextView({ file }: { file: ViewableFile }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["file-viewer-text", file.file_url],
    // ``file_url`` carries its own token, so a plain fetch (no credentials)
    // streams the bytes - the same never-expiring URL the browser would open.
    queryFn: async () => {
      const res = await fetch(file.file_url!);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    },
    enabled: hasSource(file),
    staleTime: Infinity,
    retry: false,
  });

  if (!hasSource(file)) {
    return <UnsupportedView file={file} reason="error" />;
  }
  if (isError) {
    return <UnsupportedView file={file} reason="error" />;
  }
  if (isLoading || data === undefined) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center bg-[var(--bg-light)]">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
      </div>
    );
  }
  return (
    <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-words bg-[var(--bg-light)] p-4 font-mono text-[12px] text-[var(--ink)]">
      {data}
    </pre>
  );
}

function UnsupportedView({
  file,
  reason,
}: {
  file: ViewableFile;
  reason: "type" | "dev" | "error" | "missing";
}) {
  const message =
    reason === "dev"
      ? "Office documents can only be previewed when the file is reachable from the public internet. In local dev the file isn't accessible to Microsoft's viewer - open it in a new tab or download instead."
      : reason === "error"
        ? "Could not load this file for inline preview."
        : reason === "missing"
          ? "This file has no URL."
          : "Inline preview isn't available for this file type. Open it in a new tab or download instead.";
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 bg-[var(--bg-light)] p-8 text-center">
      <FileText className="h-10 w-10 text-[var(--ink-3)]" />
      <div className="text-[12px] text-[var(--ink-2)] max-w-md">{message}</div>
      {file.file_url ? (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => openFileInNewTab(file)}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open in new tab
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => downloadFile(file)}
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function downloadFile(file: ViewableFile) {
  // ``file_url`` is the stable, never-expiring URL (TA-23). Appending
  // ``download=1`` flips the proxy's Content-Disposition to ``attachment`` so a
  // plain anchor saves the file with its name - no fetch, no blob.
  const href = fileDownloadUrl(file.file_url);
  if (!href) return;
  const a = document.createElement("a");
  a.href = href;
  a.download = file.file_name || "";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function openFileInNewTab(file: ViewableFile) {
  if (file.file_url) {
    window.open(file.file_url, "_blank", "noopener,noreferrer");
  }
}

export function FileViewerDialog({
  file,
  onOpenChange,
  onPrevious,
  onNext,
  position,
  caption,
}: {
  file: ViewableFile | null;
  onOpenChange: (open: boolean) => void;
  /**
   * Step to the neighbouring file. Omitted (or undefined at an end) hides the
   * control - a reviewer working through a claim's evidence should not have to
   * close the viewer between files, and the sequence runs across categories
   * rather than stopping at each one (WPS-140 #7).
   */
  onPrevious?: () => void;
  onNext?: () => void;
  /** 1-based position in the sequence, so the reader knows where they are. */
  position?: { index: number; total: number };
  /**
   * What this file is, in the caller's terms - stepping through a sequence
   * that spans groups, the filename alone does not say which one you are in.
   */
  caption?: string;
}) {
  const open = !!file;
  const { t } = useTranslation();
  const [fullScreen, setFullScreen] = useState(false);

  // Arrow keys are what a reader reaches for in a viewer. Bound while it is
  // open only, and skipped when focus is in a field so typing is unaffected.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      const el = document.activeElement as HTMLElement | null;
      if (
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable)
      ) {
        return;
      }
      const step = event.key === "ArrowLeft" ? onPrevious : onNext;
      if (!step) return;
      event.preventDefault();
      step();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onPrevious, onNext]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        bare
        showCloseButton={false}
        className={cn(
          "flex flex-col overflow-hidden transition-[width,height,max-width,max-height] duration-150",
          fullScreen
            ? "!max-w-[100vw] !max-h-[100vh] w-screen h-screen rounded-none"
            : "!max-w-[95vw] !max-h-[95vh] w-[95vw] h-[95vh]",
        )}
      >
        {/* Header bar: title flush left, every top control flush right
          (fullscreen, Open, Download, close). The body below is edge-to-edge
          so a zoomed image can reach the modal border. */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b-[0.5px] border-[var(--rule)] py-2 pl-3 pr-2">
          <div className="flex min-w-0 items-center gap-2">
            <DialogTitle className="truncate text-[13px] font-normal">
              {file?.file_name ?? "Preview"}
            </DialogTitle>
            {caption ? (
              <>
                <span className="shrink-0 text-[var(--ink-3)]">·</span>
                <span className="shrink-0 truncate text-[12px] text-[var(--ink-3)]">
                  {caption}
                </span>
              </>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {/* Anchored with the other controls rather than beside the title:
                the caption's width changes from file to file, which slid the
                arrows sideways as the reader stepped through. */}
            {position && position.total > 1 ? (
              <>
                <div className="flex shrink-0 items-center gap-0.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={onPrevious}
                    disabled={!onPrevious}
                    aria-label={t("common.previous", "Previous")}
                  >
                    <ChevronLeft className="h-3 w-3" />
                  </Button>
                  <span className="min-w-[42px] text-center text-[11px] tabular-nums text-[var(--ink-3)]">
                    {position.index} / {position.total}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={onNext}
                    disabled={!onNext}
                    aria-label={t("common.next", "Next")}
                  >
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
                <div className="mx-0.5 h-4 w-px bg-[var(--rule)]" />
              </>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => setFullScreen((v) => !v)}
              title={
                fullScreen
                  ? t("common.exitFullScreen", "Exit full screen")
                  : t("common.fullScreen", "Full screen")
              }
              aria-label={
                fullScreen
                  ? t("common.exitFullScreen", "Exit full screen")
                  : t("common.enterFullScreen", "Enter full screen")
              }
            >
              {fullScreen ? (
                <Minimize2 className="h-3 w-3" />
              ) : (
                <Maximize2 className="h-3 w-3" />
              )}
            </Button>
            {file && file.file_url ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={() => openFileInNewTab(file)}
                  title="Open in new tab"
                >
                  <ExternalLink className="h-3 w-3" />
                  Open
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={() => downloadFile(file)}
                  title="Download"
                >
                  <Download className="h-3 w-3" />
                  Download
                </Button>
              </>
            ) : null}
            <div className="mx-0.5 h-4 w-px bg-[var(--rule)]" />
            <DialogClose
              render={<Button type="button" variant="ghost" size="icon-sm" />}
              aria-label={t("common.close", "Close")}
            >
              <X className="h-4 w-4" />
            </DialogClose>
          </div>
        </div>
        <div className="relative flex min-h-0 flex-1 flex-col">
          {file ? <FileViewerBody file={file} /> : null}
          {onPrevious ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onPrevious}
              aria-label={t("common.previous", "Previous")}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-[var(--bg)]/85 shadow-[0_1px_4px_rgba(26,26,24,0.2)] hover:bg-[var(--bg)]"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          ) : null}
          {onNext ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onNext}
              aria-label={t("common.next", "Next")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-[var(--bg)]/85 shadow-[0_1px_4px_rgba(26,26,24,0.2)] hover:bg-[var(--bg)]"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function FileViewerProvider({ children }: { children: ReactNode }) {
  const [file, setFile] = useState<ViewableFile | null>(null);
  const value = useMemo<FileViewerContextValue>(
    () => ({
      openFile: (f) => setFile(f),
    }),
    [],
  );
  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) setFile(null);
  }, []);
  return (
    <FileViewerContext.Provider value={value}>
      {children}
      <FileViewerDialog file={file} onOpenChange={handleOpenChange} />
    </FileViewerContext.Provider>
  );
}

function FileViewerBody({ file }: { file: ViewableFile }) {
  if (!hasSource(file)) {
    return <UnsupportedView file={file} reason="missing" />;
  }
  const kind = classify(file);
  switch (kind) {
    case "image":
      return <ImageView file={file} />;
    case "pdf":
      return <PdfView file={file} />;
    case "video":
      return <VideoView file={file} />;
    case "audio":
      return <AudioView file={file} />;
    case "office":
      return <OfficeView file={file} />;
    case "text":
      return <TextView file={file} />;
    default:
      return <UnsupportedView file={file} reason="type" />;
  }
}
